import type { Dispatch, SetStateAction } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Grid } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { SelectCenter, SelectSupplier, SelectItem, SearchDateRange, StockDetailFileDownloadUpload, MessageInfo } from 'components/commonparts'
import _ from 'lodash'
import { useSearchParamStore } from 'store/searchParamStore'
import { getLocalSearchParam } from 'utils/getLocalSearchParam'

import type { ResponseResult } from 'api-client'

interface StockDetailProps {
  // Search conditions
  trCompanyId?: number | null | undefined
  centerIdList?: number[]
  orgCenterIdList?: string[]
  orgSupplierId?: string | null | undefined
  itemCode?: string | null | undefined
  dateRangeStartDate?: Date | null | undefined
  dateRangeEndDate?: Date | null | undefined
  searchWeekNum?: number | null | undefined
  
  // Output events
  resultMessage?: ResponseResult | undefined
  setResultMessage: Dispatch<SetStateAction<ResponseResult | undefined>>
  onStockFileUploadEnd?: () => void
  onCsvDownload?: () => void
  onExcelDownload?: () => void
  
  // Loading states
  isCsvLoading?: boolean
  isExcelLoading?: boolean
  isUploadLoading?: boolean
}

function StockDetail({
  resultMessage,
  trCompanyId,
  centerIdList,
  orgCenterIdList,
  orgSupplierId,
  itemCode,
  dateRangeStartDate,
  dateRangeEndDate,
  searchWeekNum,
  setResultMessage,
  onStockFileUploadEnd,
  onCsvDownload,
  onExcelDownload,
  isCsvLoading = false,
  isExcelLoading = false,
  isUploadLoading = false,
}: StockDetailProps) {
  const { loginUserInfo, typeCode } = useAuthContext()
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

  // State for search conditions
  const [centerId, setCenterId] = useState<number | null | undefined>(undefined)
  const [orgCenterId, setOrgCenterId] = useState<string | null | undefined>(undefined)
  const [centerInfo, setCenterInfo] = useState<any>(undefined)
  const [centerIdListState, setCenterIdListState] = useState<number[]>([])
  const [orgCenterIdListState, setOrgCenterIdListState] = useState<string[]>([])
  const [centerInfoList, setCenterInfoList] = useState<any[]>([])
  
  const [supplierInfo, setSupplierInfo] = useState<any>(undefined)
  const [orgSupplierIdState, setOrgSupplierIdState] = useState<string | null | undefined>(orgSupplierId)
  const [orgSupplierIdListState, setOrgSupplierIdListState] = useState<string[]>([])
  const [supplierInfoList, setSupplierInfoList] = useState<any[]>([])
  const [centerSupplierInfo, setCenterSupplierInfo] = useState<any>(undefined)
  const [centerSupplierInfoList, setCenterSupplierInfoList] = useState<any[]>([])
  
  const [itemCodeState, setItemCodeState] = useState<string | null | undefined>(itemCode)
  const [orgItemCodeState, setOrgItemCodeState] = useState<string | null | undefined>(undefined)
  const [itemInfo, setItemInfo] = useState<any>(undefined)
  const [itemCodeListState, setItemCodeListState] = useState<string[]>([])
  const [orgItemCodeListState, setOrgItemCodeListState] = useState<string[]>([])
  const [itemInfoList, setItemInfoList] = useState<any[]>([])
  
  // Date range states
  const [searchDateRangeStartType, setSearchDateRangeStartType] = useState<number | null | undefined>(undefined)
  const [searchDateRangeEndType, setSearchDateRangeEndType] = useState<number | null | undefined>(undefined)
  const [searchDateRangeStartDate, setSearchDateRangeStartDate] = useState<Date | null | undefined>(dateRangeStartDate)
  const [searchDateRangeEndDate, setSearchDateRangeEndDate] = useState<Date | null | undefined>(undefined)
  const [searchWeekNumState, setSearchWeekNumState] = useState<number | null | undefined>(searchWeekNum)

  // Validation states
  const [centerIsValid, setCenterIsValid] = useState(true)
  const [supplierIsValid, setSupplierIsValid] = useState(true)
  const [itemCloseSignal, setItemCloseSignal] = useState(0)
  const supplierIdsOnPanelOpenRef = useRef<string | null>(null)

  // Initial date range values
  const initialDateRangeValues = useMemo<{ start: string | undefined, end: string | undefined }>(() => {
    if (!typeCode || typeCode.length === 0) {
      return { start: undefined, end: undefined };
    }
    const startGroup = _.find(typeCode, { typeGroup: 'search_date_range_start' });
    const defaultStartCode = _.find(startGroup?.codes)?.typeCode;

    const endGroup = _.find(typeCode, { typeGroup: 'search_date_range_end' });
    const defaultEndCode = _.find(endGroup?.codes)?.typeCode;

    return { start: defaultStartCode, end: defaultEndCode };
  }, [typeCode]);

  const companyIdForStorage = useMemo(() => {
    if (trCompanyId !== null && trCompanyId !== undefined) return trCompanyId
    return loginUserInfo?.companyId
  }, [loginUserInfo?.companyId, trCompanyId])

  const localSearchParam = useMemo(
    () => getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, companyIdForStorage),
    [companyIdForStorage, loginUserInfo?.tenantId, searchParamState],
  )

  // Update states when props change
  useEffect(() => {
    if (trCompanyId !== undefined) {
      // Reset center selection when company changes
      setCenterId(undefined)
      setOrgCenterId(undefined)
      setCenterIdListState([])
      setOrgCenterIdListState([])
      setCenterInfoList([])
      // Reset supplier selection when company changes
      setOrgSupplierIdState(undefined)
      setOrgSupplierIdListState([])
      setSupplierInfoList([])
      setCenterSupplierInfo(undefined)
      setCenterSupplierInfoList([])
      // Reset item selection when company changes
      setItemCodeState(undefined)
      setOrgItemCodeState(undefined)
      setItemCodeListState([])
      setOrgItemCodeListState([])
      setItemInfoList([])
      setItemInfo(undefined)
    }
  }, [trCompanyId])

  useEffect(() => {
    const saved = localSearchParam?.uploadRecordStockDetail
    if (!saved) return

    setCenterIdListState(saved.centerIds ?? [])
    setOrgSupplierIdListState(saved.orgSupplierIds ?? [])
    setItemCodeListState(saved.itemCodes ?? [])
    setOrgItemCodeListState(saved.orgItemCodes ?? [])

    setCenterId(saved.centerIds?.length === 1 ? saved.centerIds[0] : undefined)
    setOrgSupplierIdState(saved.orgSupplierIds?.length === 1 ? saved.orgSupplierIds[0] : undefined)
    setItemCodeState(saved.itemCodes?.length === 1 ? saved.itemCodes[0] : undefined)
    setOrgItemCodeState(saved.orgItemCodes?.length === 1 ? saved.orgItemCodes[0] : undefined)

    // Restore SearchDateRange
    if (saved.searchDateRangeStartTypeCode != null && saved.searchDateRangeStartTypeCode !== '') {
      const v = Number(saved.searchDateRangeStartTypeCode)
      setSearchDateRangeStartType(Number.isNaN(v) ? undefined : v)
    }
    if (saved.searchDateRangeEndTypeCode != null && saved.searchDateRangeEndTypeCode !== '') {
      const v = Number(saved.searchDateRangeEndTypeCode)
      setSearchDateRangeEndType(Number.isNaN(v) ? undefined : v)
    }
    if (saved.searchDateRangeStartDate) {
      const d = new Date(saved.searchDateRangeStartDate)
      setSearchDateRangeStartDate(Number.isNaN(d.getTime()) ? undefined : d)
    }
    if (saved.searchDateRangeEndDate) {
      const d = new Date(saved.searchDateRangeEndDate)
      setSearchDateRangeEndDate(Number.isNaN(d.getTime()) ? undefined : d)
    }
    if (saved.searchWeekNum !== undefined) {
      setSearchWeekNumState(saved.searchWeekNum)
    }
  }, [localSearchParam?.uploadRecordStockDetail])

  useEffect(() => {
    if (orgSupplierId !== undefined) {
      setOrgSupplierIdState(orgSupplierId)
    }
  }, [orgSupplierId])

  useEffect(() => {
    if (itemCode !== undefined) {
      setItemCodeState(itemCode)
    }
  }, [itemCode])

  useEffect(() => {
    if (dateRangeStartDate !== undefined) {
      setSearchDateRangeStartDate(dateRangeStartDate)
    }
  }, [dateRangeStartDate])

  useEffect(() => {
    if (dateRangeEndDate !== undefined) {
      setSearchDateRangeEndDate(dateRangeEndDate)
    }
  }, [dateRangeEndDate])

  useEffect(() => {
    if (searchWeekNum !== undefined) {
      setSearchWeekNumState(searchWeekNum)
    }
  }, [searchWeekNum])

  const supplierIdsKey = useCallback((ids: string[]) => [...ids].sort().join('\u0001'), [])

  const clearItemSelection = useCallback((options?: { closeDropdown?: boolean }) => {
    setItemCodeState(undefined)
    setOrgItemCodeState(undefined)
    setItemCodeListState([])
    setOrgItemCodeListState([])
    setItemInfo(undefined)
    setItemInfoList([])
    if (options?.closeDropdown !== false) {
      setItemCloseSignal((n) => n + 1)
    }
  }, [])

  const handleSupplierDropdownOpen = useCallback((ids: string[]) => {
    supplierIdsOnPanelOpenRef.current = supplierIdsKey(ids)
  }, [supplierIdsKey])

  /** 仕入先リスト変更後にドロップダウンを閉じたときだけ商品をクリア（StockCenter と同様） */
  const handleSupplierDropdownClose = useCallback((ids: string[]) => {
    const snapshot = supplierIdsOnPanelOpenRef.current
    supplierIdsOnPanelOpenRef.current = null
    if (snapshot == null) {
      return
    }
    if (snapshot !== supplierIdsKey(ids)) {
      queueMicrotask(() => clearItemSelection({ closeDropdown: false }))
    }
  }, [supplierIdsKey, clearItemSelection])

  /** チップ削除時は即商品クリア */
  const handleSupplierChipRemoved = useCallback(() => {
    supplierIdsOnPanelOpenRef.current = null
    clearItemSelection()
  }, [clearItemSelection])

  const persistSearchParam = useMemo(
    () => () => {
      if (!loginUserInfo?.tenantId) return
      if (!companyIdForStorage) return

      setSearchParamState(loginUserInfo.tenantId, companyIdForStorage, 'UploadRecordStockDetail', {
        companyId: companyIdForStorage,
        centerIds: centerIdListState,
        orgSupplierIds: orgSupplierIdListState,
        itemCodes: itemCodeListState,
        orgItemCodes: orgItemCodeListState,
        searchDateRangeStartTypeCode: searchDateRangeStartType != null ? String(searchDateRangeStartType) : undefined,
        searchDateRangeEndTypeCode: searchDateRangeEndType != null ? String(searchDateRangeEndType) : undefined,
        searchDateRangeStartDate: searchDateRangeStartDate ? searchDateRangeStartDate.toISOString() : undefined,
        searchDateRangeEndDate: searchDateRangeEndDate ? searchDateRangeEndDate.toISOString() : undefined,
        searchWeekNum: searchWeekNumState ?? undefined,
      })
    },
    [
      centerIdListState,
      companyIdForStorage,
      itemCodeListState,
      loginUserInfo?.tenantId,
      orgItemCodeListState,
      orgSupplierIdListState,
      searchDateRangeEndDate,
      searchDateRangeEndType,
      searchDateRangeStartDate,
      searchDateRangeStartType,
      searchWeekNumState,
      setSearchParamState,
    ],
  )

  const handleCsvDownload = useMemo(
    () => () => {
      persistSearchParam()
      onCsvDownload?.()
    },
    [onCsvDownload, persistSearchParam],
  )

  const handleExcelDownload = useMemo(
    () => () => {
      persistSearchParam()
      onExcelDownload?.()
    },
    [onExcelDownload, persistSearchParam],
  )

  return (
    <StockDetailFileDownloadUpload
      // Search conditions
      trCompanyId={trCompanyId}
      centerIdList={centerIdListState}
      orgCenterIdList={orgCenterIdListState}
      orgSupplierId={orgSupplierIdState}
      itemCode={itemCodeState}
      itemCodes={itemCodeListState}
      orgSupplierIds={orgSupplierIdListState}
      dateRangeStartDate={searchDateRangeStartDate}
      dateRangeEndDate={searchDateRangeEndDate}
      searchWeekNum={searchWeekNumState}
      
      // Output events
      setResultMessage={setResultMessage}
      onStockFileUploadEnd={onStockFileUploadEnd}
      onCsvDownload={handleCsvDownload}
      onExcelDownload={handleExcelDownload}
      
      // Loading states
      isCsvLoading={isCsvLoading}
      isExcelLoading={isExcelLoading}
      isUploadLoading={isUploadLoading}
      
      // Configuration
      title="入出荷在庫明細"
      showUpload
      showDownload
      requireCenter={false}
    >
      {/* Search Conditions inside the StockDetailFileDownloadUpload */}
    <Grid container alignItems="center" sx={{ ml: -0.5 }}>
      <Grid>
        <SearchDateRange
          SearchDateRangeStart={searchDateRangeStartType !== null && searchDateRangeStartType !== undefined ? String(searchDateRangeStartType) : initialDateRangeValues.start}
          SearchDateRangeEnd={searchDateRangeEndType !== null && searchDateRangeEndType !== undefined ? String(searchDateRangeEndType) : initialDateRangeValues.end}
          isShortRange={false}
          setSearchDateRangeStartType={setSearchDateRangeStartType}
          setSearchDateRangeEndType={setSearchDateRangeEndType}
          setSearchDateRangeStartDate={setSearchDateRangeStartDate}
          setSearchDateRangeEndDate={setSearchDateRangeEndDate}
          setSearchWeekNum={setSearchWeekNumState}
        />
      </Grid>
      <Grid>
          <MessageInfo result={resultMessage} />
      </Grid>
    </Grid>
    <Grid container alignItems="center" sx={{ mb: 1 }}>
        <Grid>
          <SelectCenter
            trCompanyId={trCompanyId}
            centerId={centerId}
            orgCenterId={orgCenterId}
            centerIds={centerIdListState}
            setCenterId={setCenterId}
            setOrgCenterId={setOrgCenterId}
            setCenterInfo={setCenterInfo}
            isRequired={false}
            setIsValid={setCenterIsValid}
            isMultiple
            setCenterIdList={setCenterIdListState}
            setOrgCenterIdList={setOrgCenterIdListState}
            setCenterInfoList={setCenterInfoList}
            startSelectTop
          />
        </Grid>
    </Grid>
    <Grid container spacing={1} sx={{ mb: 2}}>
        <Grid>
          <SelectSupplier
            trCompanyId={trCompanyId ?? undefined}
            orgSupplierId={orgSupplierIdState}
            orgCenterId={orgCenterIdListState.length === 1 ? orgCenterIdListState[0] : undefined}
            centerId={centerIdListState.length === 1 ? centerIdListState[0] : undefined}
            centerIdList={centerIdListState.length > 1 ? centerIdListState : undefined}
            orgCenterIdList={orgCenterIdListState.length > 1 ? orgCenterIdListState : undefined}
            setOrgSupplierId={setOrgSupplierIdState}
            setSupplierInfo={setSupplierInfo}
            setCenterSupplierInfo={setCenterSupplierInfo}
            isRequred={false}
            setIsValid={setSupplierIsValid}
            alignmentFlag
            isMultiple
            orgSupplierIdList={orgSupplierIdListState}
            setOrgSupplierIdList={setOrgSupplierIdListState}
            setSupplierInfoList={setSupplierInfoList}
            setCenterSupplierInfoList={setCenterSupplierInfoList}
            startSelectTop
            onMultipleDropdownOpen={handleSupplierDropdownOpen}
            onMultipleDropdownClose={handleSupplierDropdownClose}
            onMultipleChipRemoved={handleSupplierChipRemoved}
          />
        </Grid>
        <Grid>
          <SelectItem
            trCompanyId={trCompanyId ?? undefined}
            itemCode={itemCodeState}
            orgItemCode={orgItemCodeState}
            setItemCode={setItemCodeState}
            setOrgItemCode={setOrgItemCodeState}
            setItemInfo={setItemInfo}
            isRequred={false}
            setIsValid={() => {}}
            isMultiple
            itemCodeList={orgItemCodeListState.length ? orgItemCodeListState : itemCodeListState}
            setItemCodeList={setItemCodeListState}
            setOrgItemCodeList={setOrgItemCodeListState}
            setItemInfoList={setItemInfoList}
            orgSupplierId={orgSupplierIdState}
            orgSupplierIdList={orgSupplierIdListState.length > 0 ? orgSupplierIdListState : undefined}
            startSelectTop
            closeDropdownSignal={itemCloseSignal}
          />
        </Grid>
        
    </Grid>
    </StockDetailFileDownloadUpload>
  )
}

export default StockDetail